import React, { useState } from "react";
import { X, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  "Spam or fake",
  "Harassment or bullying",
  "Hate speech",
  "Sexual or violent content",
  "Self-harm or dangerous",
  "Other",
];

export default function ReportDialog({ open, onClose, targetType, targetId, targetLabel }) {
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason: `${reason}${details ? ` — ${details}` : ""}`,
      });
      toast.success("Report sent. Our team will review it.");
      onClose();
      setReason(null); setDetails("");
    } catch (e) {
      toast.error("Couldn't send report");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-end sm:items-center justify-center" data-testid="report-dialog">
      <div className="w-full max-w-md rounded-t-3xl sm:rounded-3xl bg-card border-t sm:border border-white/10 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="duotone" className="text-primary" />
            <h3 className="font-heading text-lg font-semibold">Report {targetType}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="report-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          {targetLabel && <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Reporting: {targetLabel}</p>}
          <div className="grid grid-cols-1 gap-2">
            {REASONS.map((r) => (
              <button
                key={r}
                onClick={() => setReason(r)}
                data-testid={`reason-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`text-left px-4 py-3 rounded-2xl border transition ${reason === r ? "border-primary bg-primary/10 text-primary" : "border-white/10 hover:border-white/25"}`}
              >
                {r}
              </button>
            ))}
          </div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Add details (optional)"
            rows={3}
            maxLength={250}
            data-testid="report-details"
            className="w-full bg-secondary/70 border border-white/10 rounded-2xl px-4 py-3 text-sm outline-none focus:border-primary/50"
          />
          <button
            disabled={!reason || submitting}
            onClick={submit}
            data-testid="report-submit"
            className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold glow-hover disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
}
